import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toPng } from "html-to-image";
import api, { resolveImageUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  ChevronLeft, ChevronRight, Loader2, Share2, Download, X, Sparkles,
  Trophy, Flame, Heart, Users, Award, TrendingUp,
} from "lucide-react";

// ---------- helpers ----------
const fmt = (n) => Number(n || 0).toLocaleString();
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ---------- slide wrapper ----------
function Slide({ children, gradient, active, "data-testid": testId }) {
  return (
    <div
      data-testid={testId}
      className={`absolute inset-0 flex flex-col items-center justify-center p-8 sm:p-12 transition-all duration-700 ease-out ${
        active ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
      }`}
      style={{ background: gradient }}
    >
      {children}
    </div>
  );
}

// ---------- slide content components ----------
function IntroSlide({ wrap, active }) {
  return (
    <Slide active={active} gradient="linear-gradient(135deg, #18181b 0%, #3f0d4a 50%, #6d1e3f 100%)" data-testid="wrapped-slide-intro">
      <Sparkles className="w-12 h-12 text-amber-300 mb-6 animate-pulse" />
      <p className="text-zinc-400 text-sm tracking-[0.3em] uppercase mb-3">Your Castfolio</p>
      <h1 className="text-7xl sm:text-9xl font-black text-white tracking-tight leading-none">Wrapped</h1>
      <p className="text-zinc-300 text-xl mt-8 text-center max-w-md">
        {wrap.user.name?.split(" ")[0] || "Collector"}, here's your last {wrap.window.days} days of diecast obsession.
      </p>
    </Slide>
  );
}

function NumbersSlide({ wrap, active }) {
  const t = wrap.totals;
  return (
    <Slide active={active} gradient="linear-gradient(160deg, #0a0a0b 0%, #1a1a2e 60%, #0d3b66 100%)" data-testid="wrapped-slide-numbers">
      <p className="text-cyan-300/80 text-sm tracking-[0.3em] uppercase mb-4">By the numbers</p>
      <div className="grid grid-cols-2 gap-6 sm:gap-10 text-center max-w-2xl">
        <div>
          <div className="text-6xl sm:text-7xl font-black text-white">{fmt(t.pieces_added)}</div>
          <div className="text-zinc-400 mt-2 text-sm">pieces added</div>
        </div>
        <div>
          <div className="text-6xl sm:text-7xl font-black text-emerald-400">{money(t.total_market_value)}</div>
          <div className="text-zinc-400 mt-2 text-sm">market value</div>
        </div>
        <div>
          <div className="text-5xl sm:text-6xl font-black text-amber-300">{money(t.total_spent)}</div>
          <div className="text-zinc-400 mt-2 text-sm">you spent</div>
        </div>
        <div>
          <div className={`text-5xl sm:text-6xl font-black ${t.value_minus_spend >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
            {t.value_minus_spend >= 0 ? "+" : ""}{money(t.value_minus_spend)}
          </div>
          <div className="text-zinc-400 mt-2 text-sm">unrealized P/L</div>
        </div>
      </div>
      <p className="text-zinc-500 text-xs mt-10">
        active on {fmt(t.active_days)} days · {fmt(t.unique_releases)} unique releases
      </p>
    </Slide>
  );
}

function TopBrandSlide({ wrap, active }) {
  const tb = wrap.top_brand;
  return (
    <Slide active={active} gradient="linear-gradient(135deg, #2d0b3e 0%, #6c1ba0 50%, #d946ef 100%)" data-testid="wrapped-slide-top-brand">
      <p className="text-fuchsia-200/80 text-sm tracking-[0.3em] uppercase mb-3">Your most-collected brand</p>
      <div className="text-center">
        <h2 className="text-5xl sm:text-7xl font-black text-white leading-none">{tb.name || "—"}</h2>
        <div className="mt-8 flex items-center justify-center gap-6 text-white/90">
          <div>
            <div className="text-4xl font-bold">{fmt(tb.count)}</div>
            <div className="text-xs uppercase tracking-wider text-white/60 mt-1">pieces</div>
          </div>
          <div className="w-px h-12 bg-white/20" />
          <div>
            <div className="text-4xl font-bold">{Math.round((tb.share || 0) * 100)}%</div>
            <div className="text-xs uppercase tracking-wider text-white/60 mt-1">of additions</div>
          </div>
          <div className="w-px h-12 bg-white/20" />
          <div>
            <div className="text-4xl font-bold">{fmt(tb.diversity)}</div>
            <div className="text-xs uppercase tracking-wider text-white/60 mt-1">brands total</div>
          </div>
        </div>
      </div>
    </Slide>
  );
}

function GrailSlide({ wrap, active }) {
  const g = wrap.biggest_grail;
  if (!g) {
    return (
      <Slide active={active} gradient="linear-gradient(135deg, #1a0f2e 0%, #5b1e6b 100%)" data-testid="wrapped-slide-grail">
        <p className="text-violet-200/70 text-sm tracking-[0.3em] uppercase mb-4">Your grail</p>
        <Trophy className="w-16 h-16 text-violet-300 mb-4" />
        <p className="text-white text-2xl text-center max-w-md">
          The next grail is still out there. {wrap.window.days < 365 ? "Keep collecting." : "2026 is your year."}
        </p>
      </Slide>
    );
  }
  return (
    <Slide active={active} gradient="linear-gradient(135deg, #1a0f2e 0%, #4c1d95 50%, #f59e0b 100%)" data-testid="wrapped-slide-grail">
      <p className="text-amber-200 text-sm tracking-[0.3em] uppercase mb-3">Your biggest grail</p>
      {g.model_image && (
        <img
          src={resolveImageUrl(g.model_image)}
          alt={g.release_name}
          className="w-48 h-48 sm:w-64 sm:h-64 object-contain rounded-2xl bg-black/30 backdrop-blur-md p-4 ring-1 ring-white/10 mb-6"
        />
      )}
      <h3 className="text-3xl sm:text-4xl font-black text-white text-center max-w-xl leading-tight">{g.release_name}</h3>
      <p className="text-zinc-200/80 mt-2">{g.brand} · {g.vehicle}</p>
      <div className="mt-6 inline-flex items-baseline gap-2 bg-black/40 backdrop-blur-md px-6 py-3 rounded-full ring-1 ring-amber-300/30">
        <span className="text-amber-200 text-xs uppercase tracking-widest">market value</span>
        <span className="text-3xl font-black text-amber-300">{money(g.estimated_market_value)}</span>
      </div>
    </Slide>
  );
}

function SocialSlide({ wrap, active }) {
  const s = wrap.social;
  return (
    <Slide active={active} gradient="linear-gradient(135deg, #042f2e 0%, #064e3b 50%, #14b8a6 100%)" data-testid="wrapped-slide-social">
      <p className="text-emerald-200/80 text-sm tracking-[0.3em] uppercase mb-6">The community felt you</p>
      <div className="grid grid-cols-2 gap-6 sm:gap-10 max-w-2xl">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center">
            <Sparkles className="w-7 h-7 text-emerald-300" />
          </div>
          <div>
            <div className="text-4xl font-black text-white">{fmt(s.hauls_posted)}</div>
            <div className="text-emerald-100/80 text-sm">hauls posted</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center">
            <Heart className="w-7 h-7 text-rose-300" />
          </div>
          <div>
            <div className="text-4xl font-black text-white">{fmt(s.likes_received)}</div>
            <div className="text-emerald-100/80 text-sm">likes earned</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center">
            <TrendingUp className="w-7 h-7 text-cyan-300" />
          </div>
          <div>
            <div className="text-4xl font-black text-white">{fmt(s.comments_received)}</div>
            <div className="text-emerald-100/80 text-sm">comments</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center">
            <Users className="w-7 h-7 text-amber-300" />
          </div>
          <div>
            <div className="text-4xl font-black text-white">{fmt(s.followers_gained)}</div>
            <div className="text-emerald-100/80 text-sm">new followers</div>
          </div>
        </div>
      </div>
    </Slide>
  );
}

function GamificationSlide({ wrap, active }) {
  const g = wrap.gamification;
  return (
    <Slide active={active} gradient="linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #f97316 100%)" data-testid="wrapped-slide-gamification">
      <p className="text-orange-200/80 text-sm tracking-[0.3em] uppercase mb-6">Level up</p>
      <div className="flex items-center gap-8 mb-8">
        <div className="text-center">
          <Award className="w-12 h-12 text-orange-300 mx-auto mb-2" />
          <div className="text-5xl font-black text-white">LV {g.level}</div>
          <div className="text-zinc-300 text-xs uppercase tracking-widest mt-1">{fmt(g.xp)} XP</div>
        </div>
        <div className="w-px h-20 bg-white/20" />
        <div className="text-center">
          <Flame className="w-12 h-12 text-rose-400 mx-auto mb-2" />
          <div className="text-5xl font-black text-white">{fmt(g.longest_streak)}</div>
          <div className="text-zinc-300 text-xs uppercase tracking-widest mt-1">peak streak</div>
        </div>
      </div>
      {g.badges_unlocked_in_window?.length > 0 ? (
        <div className="text-center max-w-md">
          <p className="text-orange-200 text-sm uppercase tracking-widest mb-2">Badges Unlocked</p>
          <div className="flex flex-wrap gap-2 justify-center">
            {g.badges_unlocked_in_window.slice(0, 8).map((b) => (
              <span key={b.badge_id} className="px-3 py-1 rounded-full bg-white/10 backdrop-blur text-white text-sm ring-1 ring-white/20">
                {(b.title || "").replace("Badge unlocked: ", "")}
              </span>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-zinc-300 text-sm max-w-md text-center">
          {fmt(g.total_badges)} badges earned all-time. Keep collecting to unlock more.
        </p>
      )}
    </Slide>
  );
}

function ArchetypeSlide({ wrap, active, shareRef }) {
  const a = wrap.archetype;
  const t = wrap.totals;
  return (
    <Slide active={active} gradient="linear-gradient(135deg, #0a0a0b 0%, #1a1a1d 100%)" data-testid="wrapped-slide-archetype">
      <div ref={shareRef} className="bg-gradient-to-br from-zinc-900 via-purple-950/40 to-rose-950/40 ring-1 ring-white/10 rounded-3xl p-8 sm:p-12 max-w-md w-full">
        <p className="text-amber-300 text-xs tracking-[0.3em] uppercase mb-3">Your collector archetype</p>
        <h2 className="text-4xl sm:text-5xl font-black text-white leading-tight">{a.name}</h2>
        <p className="text-zinc-300 mt-4 text-base">{a.tagline}</p>
        <div className="grid grid-cols-2 gap-4 mt-8 pt-8 border-t border-white/10">
          <div>
            <div className="text-3xl font-black text-white">{fmt(t.pieces_added)}</div>
            <div className="text-zinc-500 text-xs uppercase tracking-wider">pieces</div>
          </div>
          <div>
            <div className="text-3xl font-black text-emerald-400">{money(t.total_market_value)}</div>
            <div className="text-zinc-500 text-xs uppercase tracking-wider">value</div>
          </div>
          <div>
            <div className="text-3xl font-black text-fuchsia-400">{wrap.top_brand.name || "—"}</div>
            <div className="text-zinc-500 text-xs uppercase tracking-wider">top brand</div>
          </div>
          <div>
            <div className="text-3xl font-black text-orange-400">LV {wrap.gamification.level}</div>
            <div className="text-zinc-500 text-xs uppercase tracking-wider">level</div>
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between">
          <div>
            <div className="text-white text-sm font-semibold">{wrap.user.name}</div>
            <div className="text-zinc-500 text-xs">@{wrap.user.profile_slug || wrap.user.user_id}</div>
          </div>
          <div className="text-right">
            <div className="text-zinc-400 text-xs uppercase tracking-widest">Castfolio</div>
            <div className="text-zinc-500 text-xs">Wrapped · {wrap.window.days}d</div>
          </div>
        </div>
      </div>
    </Slide>
  );
}

// ---------- main page ----------
export default function WrappedPage() {
  const navigate = useNavigate();
  const [wrap, setWrap] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0);
  const [exporting, setExporting] = useState(false);
  const shareRef = useRef(null);
  const slides = wrap ? [
    IntroSlide, NumbersSlide, TopBrandSlide, GrailSlide, SocialSlide, GamificationSlide, ArchetypeSlide,
  ] : [];
  const totalSteps = slides.length;

  useEffect(() => {
    api.get("/wrapped").then((r) => setWrap(r.data)).catch((e) => {
      setError(e?.response?.data?.detail || "Failed to load Wrapped");
    });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") setStep((s) => Math.min(s + 1, totalSteps - 1));
      if (e.key === "ArrowLeft") setStep((s) => Math.max(s - 1, 0));
      if (e.key === "Escape") navigate("/dashboard");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [totalSteps, navigate]);

  const handleShare = async () => {
    if (!shareRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(shareRef.current, { pixelRatio: 2, backgroundColor: "#0a0a0b" });
      const link = document.createElement("a");
      link.download = `castfolio-wrapped-${wrap.user.profile_slug || wrap.user.user_id}.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) {
      console.error(e);
    } finally {
      setExporting(false);
    }
  };

  if (error) {
    return (
      <div className="fixed inset-0 bg-zinc-950 flex flex-col items-center justify-center text-center p-8">
        <p className="text-rose-400 mb-4">{error}</p>
        <Button onClick={() => navigate("/dashboard")} data-testid="wrapped-error-back-btn">Back to Dashboard</Button>
      </div>
    );
  }

  if (!wrap) {
    return (
      <div className="fixed inset-0 bg-zinc-950 flex items-center justify-center" data-testid="wrapped-loading">
        <Loader2 className="w-10 h-10 text-zinc-400 animate-spin" />
      </div>
    );
  }

  const isLast = step === totalSteps - 1;

  return (
    <div className="fixed inset-0 bg-black overflow-hidden text-white" data-testid="wrapped-page">
      {/* Progress bars */}
      <div className="absolute top-0 left-0 right-0 z-30 flex gap-1 p-3">
        {slides.map((Slide, i) => (
          <div key={`progress-${Slide.name || i}`} className="flex-1 h-0.5 bg-white/15 rounded-full overflow-hidden">
            <div
              className={`h-full bg-white transition-all duration-500 ${i < step ? "w-full" : i === step ? "w-full" : "w-0"}`}
            />
          </div>
        ))}
      </div>

      {/* Close */}
      <button
        onClick={() => navigate("/dashboard")}
        data-testid="wrapped-close-btn"
        className="absolute top-5 right-5 z-30 w-10 h-10 rounded-full bg-white/10 backdrop-blur hover:bg-white/20 flex items-center justify-center transition-colors"
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Slides */}
      <div className="absolute inset-0">
        {slides.map((SlideComp, i) => (
          <SlideComp key={`slide-${SlideComp.name || i}`} wrap={wrap} active={step === i} shareRef={i === slides.length - 1 ? shareRef : null} />
        ))}
      </div>

      {/* Tap zones for navigation */}
      <button
        onClick={() => setStep((s) => Math.max(s - 1, 0))}
        data-testid="wrapped-prev-zone"
        className="absolute left-0 top-12 bottom-24 w-1/3 z-10"
        aria-label="Previous"
      />
      <button
        onClick={() => setStep((s) => Math.min(s + 1, totalSteps - 1))}
        data-testid="wrapped-next-zone"
        className="absolute right-0 top-12 bottom-24 w-1/3 z-10"
        aria-label="Next"
      />

      {/* Bottom controls */}
      <div className="absolute bottom-0 left-0 right-0 z-20 px-5 pt-5 pb-16 sm:pb-5 flex items-center justify-between gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setStep((s) => Math.max(s - 1, 0))}
          disabled={step === 0}
          className="text-white/80 hover:text-white hover:bg-white/10"
          data-testid="wrapped-prev-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>

        {isLast ? (
          <Button
            onClick={handleShare}
            disabled={exporting}
            className="bg-white text-black hover:bg-white/90 font-semibold mr-0 sm:mr-44"
            data-testid="wrapped-share-btn"
          >
            {exporting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Exporting…</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> Save my Wrapped</>
            )}
          </Button>
        ) : (
          <Button
            onClick={() => setStep((s) => Math.min(s + 1, totalSteps - 1))}
            className="bg-white text-black hover:bg-white/90 font-semibold mr-0 sm:mr-44"
            data-testid="wrapped-next-btn"
          >
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}
